import { Menu } from '@/dto/menu';
import { Marker } from '@/dto/marker';
import { Category } from '@/dto/category';
import { RECOMMENDED_CATEGORY_ID, RECOMMENDED_MARKER_NAME } from '@/constants/itemMarkers';
import { Item } from '@/dto/item';


export const isRecommendedMarker = (marker: Marker | undefined): boolean => {
  if (!marker) return false;

  return marker.name.toLowerCase() === RECOMMENDED_MARKER_NAME.toLowerCase()
    && marker.isActive
    && marker.isSystemMarker;
};

export const getRecommendedMarker = (menu: Menu | null): Marker | undefined => {
   if (!menu?.markers) return undefined;

  return Object.values(menu.markers).find(isRecommendedMarker)
}

export const getMarkerDisplayName = (marker: Marker | undefined): string => {
  return marker?.displayName ?? "";
}


export const getRecommendedCategory = (menu: Menu | null): Category | undefined => {
  const marker = getRecommendedMarker(menu);
  if (!marker) return undefined;

  const items = getItemIdsForMarker(menu, marker.id);
  if (items.length === 0) return undefined;

  return {
    id: RECOMMENDED_CATEGORY_ID,
    name: getMarkerDisplayName(marker),
    items,
    sortOrder: -1,
  };
};

const isItemEligibleForMarker = (item: Item | undefined, markerId: string): boolean => {
  if (!item) return false;

  const hasInventory = item.stockCount == null || item.stockCount > 0;
  const isAvailable = item?.available ?? false;
  
  return isAvailable && hasInventory && Boolean(item.markerIds?.includes(markerId));
};

export const getItemIdsForMarker = (menu: Menu | null, markerId: string): string[] => {
  if (!menu) return [];

  const itemsById = new Map(menu.items.map(item => [item.id, item]));
  const sortedCategories = Object.values(menu.categories).sort((a, b) => a.sortOrder - b.sortOrder);

  const itemIds: string[] = [];
  const seenItemIds = new Set<string>();

  for (const category of sortedCategories) {
    for (const itemId of category.items ?? []) {
      if (seenItemIds.has(itemId)) continue;
      seenItemIds.add(itemId);

      if (isItemEligibleForMarker(itemsById.get(itemId), markerId)) {
        itemIds.push(itemId);
      }
    }
  }

  // Uncategorized items are not referenced by any category, but should still
  // be eligible for the synthetic Recommended category.
  for (const item of menu.items) {
    if (seenItemIds.has(item.id) || (item.categoryIds?.length ?? 0) > 0) continue;

    if (isItemEligibleForMarker(item, markerId)) {
      itemIds.push(item.id);
    }
  }

  return itemIds;
};

